import { css } from '@emotion/react';
import { faMap } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useMemo,
} from 'react';
import {
  ButtonComponent, ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { UnderlinedLinkComponent } from '~/_shared/baseComponents/links';
import { EmptyStateComponent } from '~/_shared/components/emptyState/emptyState.component';
import { useCreateNewMap } from '~/_shared/components/prepareDataModal/useCreateNewMap';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { MapListingItem } from '~/map/listing/item/mapListingItem';
import { useModal } from '~/modal/useModal.hook';
import { useMapListSearchQuerySelector } from '~/store/frontendState/mapListing/mapListing.selectors';
import { useCanUserInitiateMigrationSelector } from '~/store/mapMigration/mapMigration.selectors';

const centerStyle = css({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const wrapperStyle = (isCompact: boolean) => css(centerStyle, {
  gap: 18,
  padding: `${isCompact ? 18 : 38}px 0`,
});

const linkStyle = (theme: Theme) => css(centerStyle, {
  color: theme.textColors.success,
  cursor: 'default',

  '&:hover': {
    color: theme.textColors.successOnHover,
  },
});

type MapListingEmptyStateProps = Readonly<{
  maps: ReadonlyArray< MapListingItem>;
}>;

export const MapListingEmptyStateComponent: FC<MapListingEmptyStateProps> = ({ maps }) => {
  const [t] = useTranslation();
  const { openModal: openWelcomeModal } = useModal('Welcome');
  const isSearchActive = !!useMapListSearchQuerySelector();
  const { canUserInitiateMigration } = useCanUserInitiateMigrationSelector();
  const { onCreateNewMap } = useCreateNewMap();
  const { openModal: openMigrationModal } = useModal('MigrateMaps');

  const onlySampleMaps = useMemo((): boolean => {
    return !maps.some(map => !map.isSample);
  }, [maps]);

  const showQuickStartLink = onlySampleMaps && !isSearchActive;
  const hasNoMaps = !maps.length;

  if (!hasNoMaps && !showQuickStartLink) {
    return null;
  }

  return (
    <div css={wrapperStyle(showQuickStartLink && !hasNoMaps)}>
      {hasNoMaps && !canUserInitiateMigration && (
        <EmptyStateComponent
          icon={faMap}
          title={isSearchActive ? t('mapListing.emptyState.title.withFilter') : t('mapListing.emptyState.title')}
          description={isSearchActive ? t('Refine your search and try again') : t('mapListing.emptyState.description')}
        />
      )}
      {hasNoMaps && !isSearchActive && !canUserInitiateMigration && (
        <ButtonComponent
          buttonStyle={ButtonStyle.Success}
          onClick={() => onCreateNewMap()}
          text={t('Create My First Map')}
        />
      )}
      {hasNoMaps && !isSearchActive && canUserInitiateMigration && (
        <ButtonComponent
          buttonStyle={ButtonStyle.Success}
          onClick={() => openMigrationModal()}
          text={t('MapMigrationMigrateMyMaps')}
        />
      )}
      {showQuickStartLink && (
        <UnderlinedLinkComponent
          css={linkStyle}
          onClick={openWelcomeModal}
        >
          {t('quickStartLink')}
        </UnderlinedLinkComponent>
      )}
    </div>
  );
};
